import { ScopeButton } from '@monorepo/shared-lib';
import { Space, Tooltip, Typography } from 'antd';
import { ScopeCheckbox } from 'components/Custom/checkbox';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';
import {
  getCaptureStatCardDescription,
  getCaptureStatCardTitle,
  isCaptureStatCardId,
} from 'utils/get-capture-stat-card-title';
import { getAvailableCardsForView } from './cards/cardRegistry';
import { CardId } from './cards/types';
import {
  DASHBOARD_CARD_DESCRIPTION_KEYS,
  DASHBOARD_CARD_TITLE_KEYS,
} from './cards/dashboardCardKeys';

const CAPTURE_CARD_DISPLAY_ORDER: CardId[] = [
  'captureFilesUploaded',
  'capturePendingReview',
  'captureReadyToPost',
  'capturePostedFiles',
];

const CATEGORY_DISPLAY_ORDER = [
  'summary',
  'capture',
  'informationCards',
  'team',
  'other',
] as const;

const { Text } = Typography;

const CustomDrawerStyle = createGlobalStyle`
  .ant-drawer-body:has(.card-selector-wrapper) {
    padding-bottom: 0 !important;
  }
`;

const Wrapper = styled.div`
  background: var(--color-layout-bg, #e9f6f7);
  font-family: Inter, sans-serif;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 110px);
  .top-section {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  .bottom-section {
    flex-shrink: 0;
    padding: 16px 0;
    margin-top: auto;
  }

  .bottom-section .ant-btn[disabled],
  .bottom-section .ant-btn[disabled]:hover,
  .bottom-section .ant-btn[disabled]:focus {
    opacity: 1 !important;
    color: var(--color-text-disabled, #6f7f80) !important;
    background: var(--color-surface, #132122) !important;
    border-color: var(--color-border, #355356) !important;
    box-shadow: none !important;
  }
`;

const CardListWrapper = styled.div<{ deduct: number }>`
  max-height: calc(100vh - ${({ deduct }) => deduct}px);
  min-height: 45px;
  overflow-y: auto;
  background: var(--color-surface-raised, #fff);
  border: 1px solid var(--color-border, #dbeae3);
  padding: 12px 8px;
  margin-bottom: 15px;
  &::-webkit-scrollbar {
    width: 6px;
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border, #dbeae3);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const CategorySection = styled.div`
  &:not(:last-child) {
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e8f0f0;
  }
`;

const CategoryHeading = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
  line-height: 18px;
  margin-bottom: 10px;
`;

const CardItem = styled.div`
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

interface CardSelectorProps {
  visibleCards: string[];
  onSave: (visibleCards: string[]) => void;
  dashboardViewType: 'manager' | 'my';
  onClose?: () => void;
}

export const CardSelector: React.FC<CardSelectorProps> = ({
  visibleCards,
  onSave,
  dashboardViewType,
  onClose,
}) => {
  const { t } = useTranslation();
  const [localVisibleCards, setLocalVisibleCards] =
    React.useState<string[]>(visibleCards);

  React.useEffect(() => {
    setLocalVisibleCards(visibleCards);
  }, [visibleCards]);

  const availableCards = getAvailableCardsForView(dashboardViewType);

  const cardsByCategory = availableCards.reduce((acc, card) => {
    const category = card.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(card);
    return acc;
  }, {} as Record<string, typeof availableCards>);

  const categoryLabels: Record<string, string> = {
    summary: t('dashboard.summary-cards'),
    informationCards: t('dashboard.information-cards'),
    capture: t('organisation.capture-dashboard-section'),
    team: t('dashboard.team'),
    other: t('dashboard.other'),
  };

  const orderedCategories = CATEGORY_DISPLAY_ORDER.filter(
    (category) => cardsByCategory[category]?.length
  );

  const handleToggle = (cardId: string, checked: boolean) => {
    if (checked) {
      // Newest selection first so last-added cards appear at the top of the dashboard
      setLocalVisibleCards((prev) => [cardId, ...prev]);
    } else {
      setLocalVisibleCards((prev) => prev.filter((id) => id !== cardId));
    }
  };

  const handleSave = () => {
    onSave(localVisibleCards);
    if (onClose) {
      onClose();
    }
  };

  const hasChanges =
    JSON.stringify([...localVisibleCards].sort()) !==
    JSON.stringify([...visibleCards].sort());

  return (
    <>
      <CustomDrawerStyle />
      <Wrapper className="card-selector-wrapper">
        <div className="top-section">
          <CardListWrapper deduct={180}>
            {orderedCategories.map((category) => {
              const cards = cardsByCategory[category];
              const sortedCards =
                category === 'capture'
                  ? [...cards].sort(
                      (a, b) =>
                        CAPTURE_CARD_DISPLAY_ORDER.indexOf(a.id) -
                        CAPTURE_CARD_DISPLAY_ORDER.indexOf(b.id)
                    )
                  : cards;

              return (
                <CategorySection key={category}>
                  <CategoryHeading>
                    {categoryLabels[category] || category}
                  </CategoryHeading>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {sortedCards.map((card) => {
                      const isVisible = localVisibleCards.includes(card.id);
                      const cardTitle = isCaptureStatCardId(card.id as CardId)
                        ? getCaptureStatCardTitle(card.id as CardId, t)
                        : t(
                            DASHBOARD_CARD_TITLE_KEYS[card.id as CardId] ||
                              `dashboard.card-title.${card.id}`
                          );
                      const cardDescription = isCaptureStatCardId(
                        card.id as CardId
                      )
                        ? getCaptureStatCardDescription(card.id as CardId, t)
                        : DASHBOARD_CARD_DESCRIPTION_KEYS[card.id as CardId]
                        ? t(DASHBOARD_CARD_DESCRIPTION_KEYS[card.id as CardId]!)
                        : undefined;

                      return (
                        <CardItem key={card.id}>
                          <Tooltip
                            title={
                              !card.removable
                                ? undefined
                                : isVisible
                                ? t('common.visible-uncheck-to-hide')
                                : t('common.hidden-check-to-show')
                            }
                          >
                            <ScopeCheckbox
                              checked={isVisible}
                              onChange={(e) =>
                                handleToggle(card.id, e.target.checked)
                              }
                              disabled={!card.removable}
                            >
                              <div>
                                <Text strong>{cardTitle}</Text>
                                {cardDescription ? (
                                  <div>
                                    <Text
                                      type="secondary"
                                      style={{ fontSize: '12px' }}
                                    >
                                      {cardDescription}
                                    </Text>
                                  </div>
                                ) : null}
                              </div>
                            </ScopeCheckbox>
                          </Tooltip>
                        </CardItem>
                      );
                    })}
                  </Space>
                </CategorySection>
              );
            })}
          </CardListWrapper>
        </div>
        <div className="bottom-section">
          <ScopeButton
            width="100%"
            height="45px"
            type="primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            {t('common.save')}
          </ScopeButton>
        </div>
      </Wrapper>
    </>
  );
};
